import type { ComponentType } from "react";

export interface AppRoute {
  /** URL path as mounted in the router. */
  path: string;
  /** Dynamic importer — powers both route-level code splitting and build-time prerendering. */
  importer: () => Promise<{ default: ComponentType }>;
  /** Fallback <title> used if server-side rendering of the route fails. */
  title: string;
  /** Fallback meta description used if server-side rendering of the route fails. */
  description: string;
  /** Included in sitemap.xml when true. */
  indexable?: boolean;
  priority?: string;
}

const CSD_DESC =
  "World-class custom software development services. Senior teams that design, build, and ship on time and on budget, with AI-integrated solutions. Schedule a call.";
const MOBILE_DESC =
  "iOS and Android app development by senior nearshore teams. Native and cross-platform, delivered on time and on budget in your time zone. Schedule a call.";
const WEB_DESC =
  "Custom website and web application design and development by senior nearshore engineers. WordPress, custom-coded sites, and web apps, delivered on time and on budget. Schedule a call.";

export const appRoutes: AppRoute[] = [
  {
    path: "/",
    importer: () => import("./pages/Home"),
    title: "Bluelight | Nearshore Software Development & Staff Augmentation",
    description:
      "Senior LATAM engineers for staff augmentation, dedicated teams, or custom software development. Top 1% of applicants, under 7% attrition, full US time zone overlap.",
    priority: "1.0",
  },
  {
    path: "/landing/bluelight-services",
    importer: () => import("./pages/BluelightServices"),
    title: "Software Development & Nearshore Talent | Bluelight",
    description:
      "Custom software development, AI solutions, and elite nearshore talent from Latin America. Get ready-to-go dev teams in days.",
    priority: "0.8",
  },
  {
    path: "/custom-software-development",
    importer: () => import("./pages/BluelightLanding"),
    title: "Custom Software Development Services | Bluelight",
    description: CSD_DESC,
    priority: "1.0",
  },
  {
    path: "/custom-software-development-dark",
    importer: () => import("./pages/BluelightLandingDark"),
    title: "Custom Software Development Services | Bluelight",
    description: CSD_DESC,
    priority: "0.6",
  },
  {
    path: "/custom-software-development-2",
    importer: () => import("./pages/CustomSoftwareDevelopment2"),
    title: "Custom Software Development Services | Bluelight",
    description: CSD_DESC,
    priority: "1.0",
  },
  {
    path: "/custom-mobile-app-development",
    importer: () => import("./pages/CustomMobileAppDevelopment"),
    title: "Mobile App Development Services | Bluelight",
    description: MOBILE_DESC,
    priority: "0.9",
  },
  {
    path: "/custom-mobile-app-development-2",
    importer: () => import("./pages/CustomMobileAppDevelopment2"),
    title: "Mobile App Development Services | Bluelight",
    description: MOBILE_DESC,
    priority: "0.9",
  },
  {
    path: "/custom-mobile-app-development-dark",
    importer: () => import("./pages/CustomMobileAppDevelopmentDark"),
    title: "Mobile App Development Services | Bluelight",
    description: MOBILE_DESC,
    priority: "0.6",
  },
  {
    path: "/nearshore-staff-augmentation",
    importer: () => import("./pages/NearshoreStaffAugmentation"),
    title: "Nearshore Staff Augmentation | Hire Vetted Engineers in 1-3 Weeks | Bluelight",
    description:
      "Augment your team with top 1% nearshore engineers, vetted through our 6-step process. 0-3 week time to hire, no recruiting fee, 2-week trial.",
    priority: "1.0",
  },
  {
    path: "/nearshore-staff-augmentation-2",
    importer: () => import("./pages/NearshoreStaffAugmentation2"),
    title: "Nearshore Software Engineers | 96% Client Retention | Bluelight Staff Augmentation",
    description:
      "Scale your engineering team with vetted senior nearshore engineers in your US time zone. Profiles in 48 hours, interviews in 3 to 5 days, onboarded in 7 to 14. Save up to 60% vs a US in-house hire.",
    priority: "1.0",
  },
  {
    path: "/hire-latam-developers",
    importer: () => import("./pages/HireLatamDevelopers"),
    title: "Hire LATAM Developers | Interview in 3 to 5 Days | Bluelight",
    description:
      "Hire senior Latin American developers on your US time zone. Top 1% of applicants, you sign off on every hire, month to month with no lock-in.",
    priority: "1.0",
  },
  {
    path: "/nearshore-staff-augmentation-dark",
    importer: () => import("./pages/NearshoreStaffAugmentationDark"),
    title: "IT Staff Augmentation Services | Bluelight",
    description:
      "Scale your team with senior, US-timezone engineers from Latin America. Vetted top 1% talent, integrated into your team in days. Risk-free 2-week trial. Book a call.",
    priority: "0.6",
  },
  {
    path: "/web-development",
    importer: () => import("./pages/WebDevelopment"),
    title: "Web Design & Development Company | Bluelight",
    description: WEB_DESC,
    priority: "0.9",
  },
  {
    path: "/web-development-2",
    importer: () => import("./pages/WebDevelopment2"),
    title: "Web Design & Development Company | Bluelight",
    description: WEB_DESC,
    priority: "0.9",
  },
  {
    path: "/web-development-dark",
    importer: () => import("./pages/WebDevelopmentDark"),
    title: "Web Design & Development Company | Bluelight",
    description: WEB_DESC,
    priority: "0.6",
  },
  {
    path: "/react-development",
    importer: () => import("./pages/ReactDevelopment"),
    title: "Top React.js Development Company | Bluelight",
    description:
      "Hire senior, vetted React and React Native developers from Latin America. US timezone, English-fluent, integrated into your team fast. Schedule a call.",
    priority: "0.9",
  },
  {
    path: "/laravel-development",
    importer: () => import("./pages/LaravelDevelopment"),
    title: "Hire Expert Laravel Developers | Bluelight",
    description:
      "Hire vetted, English-fluent Laravel developers from Latin America. Secure, scalable PHP applications, built in your timezone. Request a consultation.",
    priority: "0.9",
  },
  {
    path: "/php-development",
    importer: () => import("./pages/PhpDevelopment"),
    title: "Hire Remote PHP Developers | PHP Development | Bluelight",
    description:
      "Hire senior, vetted PHP developers from Latin America. Laravel, Symfony, CodeIgniter and CakePHP experts in your timezone, English-fluent, onboarded in days.",
    priority: "0.9",
  },
  {
    path: "/javascript-development",
    importer: () => import("./pages/JavascriptDevelopment"),
    title: "Hire JavaScript Developers | JavaScript Development | Bluelight",
    description:
      "Hire senior, vetted JavaScript developers from Latin America. React, Node, TypeScript experts in your time zone, English-fluent, onboarded in days.",
    priority: "0.9",
  },
  {
    path: "/node-development",
    importer: () => import("./pages/NodeDevelopment"),
    title: "Hire Node.js Developers | Node Development | Bluelight",
    description:
      "Hire senior, vetted Node.js developers from Latin America. Scalable APIs and backends built in your time zone, English-fluent, onboarded in days.",
    priority: "0.9",
  },
  {
    path: "/landing/hire-software-developers",
    importer: () => import("./pages/HireSoftwareDevelopers"),
    title: "Hire Top Software Developers and Engineering Teams | Bluelight",
    description:
      "Hire top software developers and full engineering teams from Latin America. Vetted, English-fluent talent in your time zone.",
    priority: "0.8",
  },
  {
    path: "/landing/tech-talent",
    importer: () => import("./pages/TechTalent"),
    title: "Hire Senior Tech Talent and Nearshore Engineering Teams | Bluelight",
    description:
      "Hire senior tech talent and nearshore engineering teams from Latin America. Vetted, English-fluent developers in your time zone.",
    priority: "0.8",
  },
  {
    path: "/landing/mobile-app-development",
    importer: () => import("./pages/MobileAppDevelopment"),
    title: "Nearshore Mobile App Development for iOS, Android, and Cross-Platform | Bluelight",
    description:
      "Nearshore mobile app development for iOS, Android and cross-platform. Senior teams delivering on time and on budget in your time zone.",
    priority: "0.8",
  },
  {
    path: "/landing/full-stack-developers",
    importer: () => import("./pages/FullStackDevelopers"),
    title: "Hire Full Stack Developers | Bluelight",
    description:
      "Hire senior full stack developers from Latin America. Vetted, English-fluent engineers working in your time zone.",
    priority: "0.8",
  },
  {
    path: "/landing/hire-laravel-developers",
    importer: () => import("./pages/LaravelDevelopers"),
    title: "Hire Senior Laravel Developers | Bluelight",
    description:
      "Hire senior Laravel developers from Latin America. Vetted, English-fluent PHP engineers working in your time zone.",
    priority: "0.8",
  },
  {
    path: "/landing/hire-react-developers",
    importer: () => import("./pages/ReactDevelopers"),
    title: "Hire Senior React Developers | Bluelight",
    description:
      "Hire senior React developers from Latin America. Vetted, English-fluent front-end engineers working in your time zone.",
    priority: "0.8",
  },
];

export const SITE_URL = "https://l.bluelight.co";
